import { Divider, Switch } from 'antd';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FanguardApiContractsClientEmailTemplateModel,
  FanguardApiContractsClientEmailTemplateModelKeyEnum,
} from 'src/api/generated';
import CustomSelect from 'src/components/CustomSelect';
import { FormItem, TabPageContentWrapper } from 'src/components/SharedDetailsComponents';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { useGlobal } from 'src/contexts/GlobalContext';
import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';

import { useCampaignDetails } from '../../CampaignDetailsContext';
import { NumberInput } from './Outreach.styled';

const generateSelectOptions = (emailTemplates: FanguardApiContractsClientEmailTemplateModel[], key: string) => {
  return emailTemplates
    .filter((template) => template.key === key)
    .map((template) => ({
      label: template.subject ?? '',
      value: template.id ?? '',
    }))
    .filter((option) => option.value && option.label);
};

export const Outreach = () => {
  const { isDesktop } = useGlobal();
  const { formMethods, campaign, emailTemplates, isEmailTemplatesLoading } = useCampaignDetails();
  const { t } = useTranslation();

  const registrationMissingPhotosTemplateId = formMethods.watch('registrationMissingPhotosTemplateId');

  if (isEmailTemplatesLoading) {
    return <SuspenseLoader />;
  }

  const emailVerificationEmailTemplatesOptions = generateSelectOptions(
    emailTemplates,
    FanguardApiContractsClientEmailTemplateModelKeyEnum.EmailVerification,
  );

  const missingPhotosEmailTemplatesOptions = generateSelectOptions(
    emailTemplates,
    FanguardApiContractsClientEmailTemplateModelKeyEnum.RegistrationReminderMissingPhotos,
  );

  const registrationCompleteEmailTemplatesOptions = generateSelectOptions(
    emailTemplates,
    FanguardApiContractsClientEmailTemplateModelKeyEnum.EnrollmentComplete,
  );

  const requestEnrollmentEmailTemplatesOptions = generateSelectOptions(
    emailTemplates,
    FanguardApiContractsClientEmailTemplateModelKeyEnum.RequestEnrollment,
  );

  const incompleteEnrollmentReminderEmailTemplatesOptions = generateSelectOptions(
    emailTemplates,
    FanguardApiContractsClientEmailTemplateModelKeyEnum.IncompleteEnrollmentReminder,
  );

  return (
    <>
      <SettingCard
        title={t('campaigns.outreach.emailTemplates')}
        description={t('campaigns.outreach.emailTemplatesDescription')}
        items={[
          {
            header: t('campaigns.outreach.authenticationEmails'),
            content: (
              <TabPageContentWrapper $isDesktop={false} data-testid="campaign-outreach-authentication-form">
                <FormItem
                  $isDesktop={false}
                  label={t('campaigns.outreach.emailVerificationTemplate')}
                  name="emailVerificationTemplateId"
                >
                  <Controller
                    name="emailVerificationTemplateId"
                    control={formMethods.control}
                    render={({ field }) => (
                      <CustomSelect
                        {...field}
                        data-testid="campaign-outreach-email-verification-template-select"
                        field={field}
                        options={emailVerificationEmailTemplatesOptions}
                        title={t('campaigns.outreach.emailVerificationTemplate')}
                        onOkText={t('general.save')}
                        onOk={field.onChange}
                        placeholder={t('campaigns.outreach.emailVerificationTemplatePlaceholder')}
                      />
                    )}
                  />
                </FormItem>
              </TabPageContentWrapper>
            ),
          },
          {
            header: t('campaigns.outreach.registrationEmails'),
            content: (
              <TabPageContentWrapper $isDesktop={false} data-testid="campaign-outreach-registration-form">
                <FormItem
                  $isDesktop={false}
                  label={t('campaigns.outreach.registrationCompleteTemplate')}
                  name="registrationCompleteTemplateId"
                >
                  <Controller
                    name="registrationCompleteTemplateId"
                    control={formMethods.control}
                    render={({ field }) => (
                      <CustomSelect
                        field={field}
                        options={registrationCompleteEmailTemplatesOptions}
                        title={t('campaigns.outreach.registrationCompleteTemplate')}
                        onOkText={t('general.save')}
                        onOk={field.onChange}
                        placeholder={t('campaigns.outreach.registrationCompleteTemplatePlaceholder')}
                        data-testid="campaign-outreach-registration-complete-template-select"
                      />
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={false}
                  label={t('campaigns.outreach.registrationMissingPhotosTemplate')}
                  name="registrationMissingPhotosTemplateId"
                >
                  <Controller
                    name="registrationMissingPhotosTemplateId"
                    control={formMethods.control}
                    render={({ field }) => (
                      <CustomSelect
                        field={field}
                        options={missingPhotosEmailTemplatesOptions}
                        title={t('campaigns.outreach.registrationMissingPhotosTemplate')}
                        onOkText={t('general.save')}
                        onOk={field.onChange}
                        placeholder={t('campaigns.outreach.registrationMissingPhotosTemplatePlaceholder')}
                        data-testid="campaign-outreach-registration-missing-photos-template-select"
                      />
                    )}
                  />
                </FormItem>
                {registrationMissingPhotosTemplateId && (
                  <FormItem
                    $isDesktop={false}
                    label={t('campaigns.outreach.noPhotoEmailReminderDelay')}
                    name="noPhotoEmailReminderDelay"
                  >
                    <Controller
                      name="noPhotoEmailReminderDelay"
                      control={formMethods.control}
                      render={({ field }) => (
                        <NumberInput {...field} data-testid="campaign-no-photo-email-reminder-delay-input" />
                      )}
                    />
                  </FormItem>
                )}
              </TabPageContentWrapper>
            ),
          },
        ]}
      />
      {isDesktop && <Divider />}
      <SettingCard
        title={t('campaigns.outreach.invitationOnly')}
        description={t('campaigns.outreach.invitationOnlyDescription')}
        items={[
          {
            content: (
              <TabPageContentWrapper $isDesktop={isDesktop} data-testid="campaign-results-page-form">
                <FormItem
                  $isDesktop={false}
                  label={t('campaigns.outreach.enrollmentByInvitationOnly')}
                  name="enrollmentByRequestOnly"
                >
                  <Controller
                    name="enrollmentByRequestOnly"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        defaultChecked={!!campaign?.enrollmentByRequestOnly}
                        data-testid="campaign-enrollment-by-invitation-only-switch"
                      />
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={false}
                  label={t('campaigns.outreach.requestEnrollmentTemplate')}
                  name="requestEnrollmentTemplateId"
                >
                  <Controller
                    name="requestEnrollmentTemplateId"
                    control={formMethods.control}
                    render={({ field }) => (
                      <CustomSelect
                        field={field}
                        options={requestEnrollmentEmailTemplatesOptions}
                        title={t('campaigns.outreach.requestEnrollmentTemplate')}
                        onOkText={t('general.save')}
                        onOk={field.onChange}
                        placeholder={t('campaigns.outreach.requestEnrollmentTemplatePlaceholder')}
                        data-testid="campaign-request-enrollment-email-template-select"
                      />
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={false}
                  label={t('campaigns.outreach.incompleteCampaignReminderDelay')}
                  name="registrationIncompleteTemplateId"
                >
                  <Controller
                    name="registrationIncompleteTemplateId"
                    control={formMethods.control}
                    render={({ field }) => (
                      <CustomSelect
                        field={field}
                        options={incompleteEnrollmentReminderEmailTemplatesOptions}
                        title={t('campaigns.outreach.incompleteCampaignReminderDelay')}
                        onOkText={t('general.save')}
                        onOk={field.onChange}
                        placeholder={t('campaigns.outreach.incompleteCampaignReminderDelayPlaceholder')}
                        data-testid="campaign-incomplete-enrollment-reminder-email-template-select"
                      />
                    )}
                  />
                </FormItem>
              </TabPageContentWrapper>
            ),
          },
        ]}
      />
    </>
  );
};
